import { Event } from "../../shared/event-system/Event";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { VariableSaver } from "../../tools/variableSaver/VariableSaver";
import { WalletType } from "./WalletType";
import { IThirdWebWallet } from "./wallets/IThirdWebWallet";
import { InAppWallet } from "./wallets/InAppWallet";
import { WalletFactory } from "./WalletFactory";
import { Web3Config } from "../config/Web3Config";
import { NFTPackage } from "../nfts/NFTPackage";
import { Chain, ThirdwebClient } from "thirdweb";
import { Account } from "thirdweb/wallets";
import { MintRequest } from "./MintRequest";
import { NFTGiftPackage } from "../nfts/NFTGiftPackage";
import { PasskeyWallet } from "./wallets/PasskeyWallet";
import { PrepareMintResultDto } from "../../cityversetycoon/networking/data/responses/marketplace/buildings/PrepareMintResultDto";
import { IConnectionModel } from "../../cityversetycoon/networking/IConnectionModel";
import { BlockchainTransactionResult } from "../BlockchainTransactionResult";
import { ThirdWebAnalytic } from "../utils/ThirdWebAnalytic";

export class ThirdWeb {
    private static readonly AUTH_TYPE_KEY = "connect_wallet_with";

    public readonly onDataChanged: Event<void>;
    
    private readonly walletFactory: WalletFactory;

    private walletValue: IThirdWebWallet<any>;

    private get wallet(): IThirdWebWallet<any> {
        return this.walletValue;
    }

    private set wallet(newWallet: IThirdWebWallet<any>) {
        this.walletValue?.onDataChanged?.remove(this.onWalletChanged, this);
        if (newWallet == null) {
            this.walletValue = null;
            return;
        }
        newWallet.onDataChanged.add(this.onWalletChanged, this);
        this.walletValue = newWallet;
    }

    public get address(): string {
        return this.wallet?.address;
    }

    public get connected(): boolean {
        return this.wallet?.connected;
    }

    public get supportedChainSelected(): boolean {
        return this.wallet?.supportedChainSelected;
    }

    public get smartWalletAddress(): string {
        return this.wallet?.smartWalletAddress;
    }

    public get chainId(): string {
        return this.wallet?.chainId;
    }

    public get account(): Account {
        return this.wallet.account;
    }

    public get email(): string {
        return this.wallet?.email ?? "";
    }

    public constructor(
        web3Config: Web3Config, 
        thirdWebclient: ThirdwebClient, 
        private readonly variableSaver: VariableSaver, 
        thirdWebAnalytic: ThirdWebAnalytic, 
        private readonly logger: ILogger
    ) {
        this.variableSaver = variableSaver;
        this.logger = logger;
        this.onDataChanged = new Event();
        this.walletFactory = new WalletFactory(web3Config, thirdWebclient, thirdWebAnalytic, logger);
    }

    public async connect(): Promise<boolean> {
        throw new Error("Unable to connect using ThirdWeb by calling connect");
    }

    public async autoConnect(): Promise<boolean> {
        try {
            if (!this.variableSaver.exist(ThirdWeb.AUTH_TYPE_KEY)) {
                return false;
            }
            const walletType = this.variableSaver.getString(ThirdWeb.AUTH_TYPE_KEY) as WalletType;
            if (walletType == null) {
                return false;
            }
            this.wallet = this.walletFactory.createWallet(walletType);
            if (await this.wallet.autoConnect()) {
                return true;
            }
        } catch (e: unknown) {
            this.logger.debug(`Unable to auto connect wallet: ${e}`);
            return false;
        }
    }

    public async updateUSDCBalance(): Promise<string> {
        return await this.wallet.updateUSDCBalance();
    }

    public async updateMONBalance(): Promise<string> {
        return await this.wallet.updateMONBalance();
    }

    public async updateErc20Balance(chain: Chain, erc20Address: string): Promise<string> {
        return await this.wallet.updateErc20Balance(chain, erc20Address);
    }

    public async getTokenSymbol(chain: Chain, erc20Address: string): Promise<string> {
        return await this.wallet.getTokenSymbol(chain, erc20Address);
    }

    public async sendVereficationEmail(email: string): Promise<boolean> {
        return await this.sendVerificationEmeilInternal(email);
    }

    public async sendPasskeyLoginRequest(): Promise<boolean> {
        return await this.sendPasskeyLoginRequestInternal();
    }

    private async sendPasskeyLoginRequestInternal(attemp: number = 0): Promise<boolean> {
        try {
            this.validateCurrentWallet(WalletType.PASSKEY);
            await (this.wallet as PasskeyWallet).sendPasskeyLoginRequest();
            return true;
        } catch (e: any) {
            this.logger.debug(`Exception on passkey login request, disconnect: ${e?.message ?? e}`);
            await this.disconnect();
            if (attemp === 0) {
                return await this.sendPasskeyLoginRequestInternal(++attemp);
            } else {
                this.logger.error("Exception on passkey login request: ", e);
                return false;
            }
        }
    }

    private async sendVerificationEmeilInternal(email: string, attemp: number = 0): Promise<boolean> {
        try {
            this.validateCurrentWallet(WalletType.EMAIL);
            await (this.wallet as InAppWallet).sendVereficationEmail(email);
            return true;
        } catch (e: any) {
            this.logger.debug(`Exception on verefication email, disconnect: ${e?.message ?? e}`);
            await this.disconnect();
            if (attemp === 0) {
                return await this.sendVerificationEmeilInternal(email, ++attemp);
            } else {
                this.logger.error("Exception on verefication email: ", e);
                return false;
            }
        }
    }

    public async createWallet(email: string, verificationCode: string): Promise<boolean> {
        try {
            this.validateCurrentWallet(WalletType.EMAIL);
            if (this.wallet.connected) {
                this.logger.debug(`Wallet already created and connected`);
                return true;
            }
            if (await (this.wallet as InAppWallet).connect({ email, verificationCode })) {
                this.variableSaver.saveString(ThirdWeb.AUTH_TYPE_KEY, WalletType.EMAIL);
                return true;
            }
            return false;
        } catch (e: unknown) {
            this.logger.error("Exception on creating wallets", e);
            return false;
        }
    }

    public async createPasskeyWallet(isNewPasskeyFlow?: boolean): Promise<boolean> {
        try {
            this.validateCurrentWallet(WalletType.PASSKEY);
            if (this.wallet.connected) {
                this.logger.debug(`Wallet already created and connected`);
                return true;
            }
            if (await (this.wallet as PasskeyWallet).connect({ isNewPasskeyFlow })) {
                this.variableSaver.saveString(ThirdWeb.AUTH_TYPE_KEY, WalletType.PASSKEY);
                return true;
            }
            return false;
        } catch (e: unknown) {
            this.logger.error("Exception on creating wallets", e);
            return false;
        }
    }

    public async signMessage(nonce: string): Promise<string> {
        return await this.wallet?.signMessage(nonce);
    }

    public async disconnect(): Promise<void> {
        this.variableSaver.saveString(ThirdWeb.AUTH_TYPE_KEY, "");
        await this.wallet?.disconnect();
    }

    public async burnNFTPackage(nftPackage: NFTPackage): Promise<string> {
        return await this.wallet.burnNFTPackage(nftPackage);
    }

    public async burnNFTGiftPackage(nftGiftPackage: NFTGiftPackage): Promise<string> {
        return await this.wallet.burnNFTGiftPackage(nftGiftPackage);
    }

    public async claimGiftNft(mintRequest: MintRequest): Promise<boolean> {
        return await this.wallet.claimGiftNft(mintRequest);
    }

    public async waitSmartWalletConnection(): Promise<void> {
        return await this.wallet.waitSmartWalletConnection();
    }

    public async hasStoredPassekey(): Promise<boolean> {
        if (!this.wallet) {
            this.wallet = this.walletFactory.createWallet(WalletType.PASSKEY);
        }

        return await (this.wallet as PasskeyWallet).hasPasskey();
    }

    public async sendErc20Tokens(chain: Chain, erc20Address: string, address: string, value: number): Promise<boolean> {
        return await this.wallet.sendErc20Tokens(chain, erc20Address, address, value);
    }

    public async approveErc20Transaction(chain: Chain, erc20Address: string, spenderAddress: string, price: number): Promise<boolean> {
        return await this.wallet.approveErc20Transaction(chain, erc20Address, spenderAddress, price);
    }
    
    public async processStorePackageClaimTransaction(chain: Chain, tokenId: bigint): Promise<BlockchainTransactionResult> {
        return await this.wallet.processErc20StorePackageClaimTransaction(chain, tokenId);
    }

    public async sendL1Erc20Tokens(erc20Address: string, address: string, value: number): Promise<boolean> {
        return await this.wallet.sendL1Erc20Tokens(erc20Address, address, value);
    }

    public async approveL1Erc20Transaction(erc20Address: string, price: number): Promise<boolean> {
        return await this.wallet.approveL1Erc20Transaction(erc20Address, price);
    }

    public async processL1StorePackageClaimTransaction(tokenId: bigint): Promise<BlockchainTransactionResult> {
        return await this.wallet.processL1StorePackageClaimTransaction(tokenId);
    }

    public async purchaseL1Building(mintRequest: PrepareMintResultDto): Promise<BlockchainTransactionResult> {
        return await this.wallet.ethL1BuildingClaimWithSignature(mintRequest);
    }

    public async purchaseBuildingWithCrypto(chain: Chain, mintRequest: PrepareMintResultDto): Promise<BlockchainTransactionResult> {
        return await this.wallet.erc20BuildingClaimWithSignature(chain, mintRequest);
    }

    private onWalletChanged(): void {
        this.logger.debug(`onExternalWalletChanged, wallet connected: ${this.connected}, supportedChainSelected: ${this.supportedChainSelected}`);
        if (!this.wallet.connected) {
            this.variableSaver.saveString(ThirdWeb.AUTH_TYPE_KEY, "");
        }
        this.onDataChanged.emit();
    }

    private validateCurrentWallet(walletType: WalletType): void {
        switch (walletType) {
            case WalletType.EMAIL:
                if (this.wallet?.connected) {
                    throw new Error(`Unable to process ${walletType} wallet operation, already connected with other wallet`);
                }
                break;
            case WalletType.PASSKEY:
                if (this.wallet?.connected) {
                    throw new Error(`Unable to process ${walletType} wallet operation, already connected with other wallet`);
                }
                break;
            default:
                throw new Error(`Unable to validate wallet with type: ${walletType}`);
        }
        this.wallet = this.walletFactory.createWallet(walletType);
    }
}
